import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Mail, Phone, Download, Fish, Shield, Droplets, Settings, Waves, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const PassiveFishScreensPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What regulatory agencies approve PSF screens, and what standards do they meet?",
      answer: "PSF screens meet specifications from Washington Department of Fish & Wildlife (WDFW), NOAA Fisheries (NMFS), California Department of Fish & Wildlife, Oregon Department of Fish & Wildlife, US Bureau of Reclamation, and US Army Corps of Engineers. Each screen is custom-fabricated to meet velocity limits (typically ≤0.4 fps), opening sizes (3/32\" or smaller), and standoff requirements for your jurisdiction. CTC filtration specialists provides approach velocity calculations and compliance documentation for permit applications."
    },
    {
      question: "How do I determine the correct PSF screen size for my flow rate?",
      answer: "Screen size is determined by your pump flow rate and regulatory approach velocity limits. Most jurisdictions require ≤0.4 fps through screen openings. We calculate required screen surface area: larger flows need larger screen area to maintain low velocity. Use the spec table to match your flow rate to the appropriate PSF model. Contact our filtration specialists for site-specific calculations, especially for variable flow applications or jurisdictions with different velocity requirements."
    },
    {
      question: "Can PSF screens be installed on existing intake pipes or only new construction?",
      answer: "PSF screens retrofit to existing installations. They mount to suction pipe ends via bolt-on collars, flanges, or threaded connections. Specify your existing pipe OD, flange type, or thread standard, and we fabricate mounting hardware to match. Most installations take 1-2 hours with basic tools. For challenging retrofits (tight access, underwater mounting), consult our filtration specialists for custom mounting solutions."
    },
    {
      question: "Do PSF screens require any power, controls, or moving parts?",
      answer: "No. Passive Fish Screens have zero moving parts, require no electrical power, no control systems, no motors, no timers. They're fixed cylindrical strainers that protect fish through proper sizing and low approach velocity. This makes them ideal for remote sites, gravity-fed systems, off-grid applications, or anywhere electrical infrastructure is not feasible. The only maintenance is occasional manual cleaning."
    },
    {
      question: "What is the difference between PSF passive screens and SCS2 self-cleaning screens?",
      answer: "PSF = Passive (no power, manual cleaning, fixed screen, simple construction). SCS2 = Active (uses pump discharge water, stationary screen with internal rotating spray bar, automatic cleaning, more complex). Choose PSF for: remote sites, low debris loading, power not available, gravity systems. Choose SCS2 for: heavy debris (leaves/algae), continuous operation, sites with pump discharge available. Both meet fish protection requirements when properly sized. PSF is lower cost but requires manual cleaning; SCS2 is automated but needs maintenance."
    },
    {
      question: "How often do PSF screens require cleaning, and what is the procedure?",
      answer: "Cleaning frequency depends on debris loading: clean water (monthly to seasonally), moderate debris (weekly to monthly), heavy organic matter (daily to weekly). Inspect regularly during operating season. Cleaning procedure: (1) Shut down pump. (2) Remove screen from water. (3) Rinse with hose or brush debris from exterior. (4) Inspect for damage. (5) Reinstall. Takes 10-30 minutes. For heavy debris sites requiring frequent cleaning, consider upgrading to SCS2 self-cleaning screens."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Passive Fish Screens (PSF) | Intake Protection | CTC Waterworks"
        description="PSF passive fish-friendly screens for low-velocity intake protection where power isn't feasible and regulations apply. No power required, robust construction, meets fish compliance standards."
        canonical="https://www.ctcwaterworks.com/filtration/suction-screens/psf"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/suction-screens" className="text-gray-600 hover:text-ctc-blue">Suction Screens</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">PSF</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Fish className="text-green-600 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      Passive Fish-Friendly Screens (PSF)
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Low-velocity intake protection where power isn't feasible and regulations apply.
                </p>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">PSF = Passive Screening & Fish Protection</h3>
                  <p className="text-gray-700 leading-relaxed">
                    PSF screens are fixed cylindrical strainers with 3/32" or smaller openings, designed to meet fish protection regulations without requiring power or moving parts. Large surface area maintains low approach velocity (typically ≤0.4 fps) to prevent fish impingement. Ideal for remote sites, gravity-fed systems, or anywhere electrical infrastructure is not feasible.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-green-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>PSF Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-green-600 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Images Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/PSF.jpeg" alt="PSF Passive Fish Screen Product" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">PSF Passive Fish Screen</h3>
                <p className="text-sm text-gray-700">Fixed cylindrical screen with fish-friendly design</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/PSF Diagram 2.jpeg" alt="PSF Installation Configuration" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Installed Configuration</h3>
                <p className="text-sm text-gray-700">PSF screen shown in typical field installation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose PSF Passive Screens?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Shield className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">No Power Required</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Zero moving parts, no electricity, no controls. Fixed cylindrical screen protects fish through proper sizing and low velocity. Ideal for off-grid and remote sites.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Fish className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Fish-Friendly Design</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Meets ≤0.4 fps approach velocity requirements. Large surface area prevents impingement. 3/32" or smaller openings. Agency-compliant for permit applications.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Settings className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Robust Construction</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                304 stainless steel construction. Multiple diameters and lengths available. Bolt-on mounting for easy installation and removal. Withstands harsh environments.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6">
              <Droplets className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">System Integration</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Works upstream of BIG or TIG inline filters for complete two-stage protection. PSF handles bulk debris, inline filter provides final polishing for emitters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">PSF Specifications & Flow Capacity</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            All models sized to meet ≤0.4 fps approach velocity with adequate submergence. Flow capacity in both GPM and CFS for hydraulic calculations.
          </p>

          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <img src="/PSF Diagram.jpeg" alt="PSF Technical Diagram" className="w-full h-auto" />
              <div className="p-4 bg-gray-50">
                <p className="text-sm text-gray-700 text-center"><span className="font-semibold">Technical Diagram:</span> PSF screen dimensions and specifications</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">GPM</th>
                  <th className="px-4 py-3 text-left font-semibold">CFS</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Dia (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Stand-Off (in)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">3"</td>
                  <td className="px-4 py-3">AA503</td>
                  <td className="px-4 py-3">75</td>
                  <td className="px-4 py-3">1/6</td>
                  <td className="px-4 py-3">3 NPTM</td>
                  <td className="px-4 py-3">6</td>
                  <td className="px-4 py-3">8½</td>
                  <td className="px-4 py-3">12</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">4"</td>
                  <td className="px-4 py-3">AA504</td>
                  <td className="px-4 py-3">150</td>
                  <td className="px-4 py-3">1/3</td>
                  <td className="px-4 py-3">4 NPTM</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">11½</td>
                  <td className="px-4 py-3">16</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">5"</td>
                  <td className="px-4 py-3">AA505</td>
                  <td className="px-4 py-3">300</td>
                  <td className="px-4 py-3">2/3</td>
                  <td className="px-4 py-3">5 FLG</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">17½</td>
                  <td className="px-4 py-3">20</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">6"</td>
                  <td className="px-4 py-3">AA506</td>
                  <td className="px-4 py-3">444</td>
                  <td className="px-4 py-3">0.99</td>
                  <td className="px-4 py-3">6 FLG</td>
                  <td className="px-4 py-3">11</td>
                  <td className="px-4 py-3">23½</td>
                  <td className="px-4 py-3">22</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Note:</span> .062 perf is the smallest recommended perf screen size.
            </p>
          </div>
        </div>
      </section>

      {/* Fish Protection Compliance */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Fish Protection & Regulatory Compliance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Fish className="mr-3 text-green-600" size={28} />
                Approach Velocity Compliance
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                PSF screens meet the ≤0.4 fps approach velocity standard required by most fish protection agencies. Large screen surface area distributes flow to maintain low velocity through openings, preventing fish impingement and allowing safe passage.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">≤0.4 fps standard (WDFW, NMFS, CDFW)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Calculated for site-specific conditions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Compliance documentation provided</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileCheck className="mr-3 text-ctc-blue" size={28} />
                Screen Opening Standards
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                PSF screens use 3/32" (0.094") perforations as standard, meeting fish exclusion requirements for most jurisdictions. Smaller openings (1/16" or 2mm) available for stricter regulations or sensitive species protection.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">3/32" standard (typical requirement)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">1/16" or 2mm for strict jurisdictions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Custom openings for special cases</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Agency Coordination:</span> CTC filtration specialists works directly with permitting authorities to ensure PSF screens meet all local, state, and federal requirements. We provide approach velocity calculations, compliance drawings, and technical documentation for your permit application. Agencies we regularly work with: WDFW, NOAA Fisheries (NMFS), CDFW, ODFW, USBR, USACE, and local irrigation districts.
            </p>
          </div>
        </div>
      </section>

      {/* Options & Accessories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Options & Accessories</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Protective Hoops</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                External reinforcing rings around screen cylinder prevent damage from floating debris, ice, or impact. Recommended for 8" models, required for 10" and larger screens exposed to debris loading.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Protects screen mesh from damage</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">304 SS construction</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Maintains stand-off clearance</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Anti-Vortex Features</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Vortex suppression plates and end caps prevent air entrainment in shallow or turbulent water conditions. Critical for applications near water surface or in high-velocity flow zones.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Prevents air entrainment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Required for shallow submergence</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Improves pump efficiency</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mounting Kits</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Custom mounting hardware for bolt-on, flange, or threaded connections. Includes lifting brackets, anchor points, and hardware matched to your existing pipe or installation method.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Matches existing pipe OD or flange</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Lifting eyes for installation/removal</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Stainless hardware included</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Custom Fabrication:</span> Every PSF screen is custom-fabricated to your specifications. Provide pipe OD, flow rate, water source details, and jurisdictional requirements—we'll engineer a compliant solution. Special configurations available for challenging installations, extreme environments, or unique regulatory requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Maintenance & Installation */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Installation & Maintenance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Settings className="mr-3 text-ctc-blue" size={28} />
                Installation Guidelines
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Position screen with adequate submergence (2× screen diameter minimum)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Orient perpendicular to flow when possible to minimize impingement</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Maintain stand-off clearances per spec table</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Secure mounting to prevent movement or rotation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Use lifting eyes for safe removal during maintenance</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Waves className="mr-3 text-green-600" size={28} />
                Maintenance Requirements
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Inspect regularly during operating season (weekly to monthly)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Clean when debris accumulation visible or flow reduced</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Remove and rinse with hose or brush exterior</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Check for damage—repair or replace damaged screens</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Heavy debris loading may require daily cleaning or SCS2 upgrade</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Compare Suction Screen Options</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/suction-screens/standard"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Standard Suction Screens</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For manual-service applications with low-flow or clean water, consider Standard Suction Screens. Simple fixed design with no moving parts.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                Compare Manual Models
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/suction-screens/scs2"
              className="bg-white border-2 border-ctc-blue rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compare SCS2 Self-Cleaning</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For heavy debris loading or automated operation, consider SCS2 screens with internal rotating spray bar cleaning. Uses pump discharge water and eliminates manual cleaning.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                View SCS2 Screens
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Add downstream BIG inline filter for two-stage protection. PSF handles bulk debris, BIG provides fine polishing (20-120 mesh) for emitter protection.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View BIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/tig"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with TIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compact true-inline TIG filters for secondary polishing after PSF. Ideal for space-constrained installations, booster stations, and fertigation skids.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                View TIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-green-600 transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-white">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Intake with Fish-Friendly Screening?</h2>
          <p className="text-xl text-green-100 mb-8">
            Our filtration specialist team will calculate approach velocities, design compliant PSF screens, and provide documentation for your permit application. Custom fabrication to meet local, state, and federal fish protection requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-green-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Screen
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-green-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PassiveFishScreensPage;
